import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { JudgementModal, StartGate, FailOverlay, MinigameHud } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

const Template14CipherV2 = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_cipherv2_14', templateId = 14 }) => {
  // R1: 既定値の世界観化。旧「KHOOR ZRUOG → HELLO WORLD」（カエサル教科書例）を、
  // 自作シャッフル換字表（K→Y / O→M / E→R / N→H / I→S / U→A）で暗号化した
  // 「YMR HM YSMYA → KOE NO KIOKU（声の記憶）」へ差し替え。
  // 位置対応で誘導される写像は Y→K, M→O, R→E, H→N, S→I, A→U の 6 文字・
  // 同一暗号字は常に同一平文（Y×3→K, M×3→O）・暗号字と平文字の集合は不交差＝
  // 換字式として整合し、タイル 6 枚を機械的に解き切れる。config 上書きは不変。
  const defaultConfig = {
    title: '暗号解読',
    originalMessage: 'YMR HM YSMYA',
    cipherType: 'substitution',
    hints: [
      { plain: 'K', hint: '暗号文の Y は、平文の K' },
      { plain: 'O', hint: '暗号文の M は、平文の O' }
    ],
    maxMistakes: 5,
    decodedMessage: 'KOE NO KIOKU'
  };

  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!next.originalMessage) next.originalMessage = defaultConfig.originalMessage;
    if (!next.decodedMessage) next.decodedMessage = defaultConfig.decodedMessage;
    return next;
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  const substitutionMap = useMemo(() => {
    const map = {};
    const original = resolvedConfig.originalMessage.toUpperCase();
    const decoded = resolvedConfig.decodedMessage.toUpperCase();
    for (let i = 0; i < original.length && i < decoded.length; i++) {
      const origChar = original[i];
      const decChar = decoded[i];
      if (/[A-Z]/.test(origChar) && /[A-Z]/.test(decChar)) {
        map[origChar] = decChar;
      }
    }
    return map;
  }, [resolvedConfig.originalMessage, resolvedConfig.decodedMessage]);

  const [userMap, setUserMap] = useState({});
  const [selectedCipher, setSelectedCipher] = useState(null);
  const [mistakes, setMistakes] = useState(0);
  const [revealedHints, setRevealedHints] = useState([]);
  const [gameState, setGameState] = useState('playing');
  const [inputValue, setInputValue] = useState('');
  const [showEffect, setShowEffect] = useState(null);
  // R1 体験文法: 開始ゲート（hostMeta.ui.showTutorial !== false で表示）と retryCount
  const [gateOpen, setGateOpen] = useState(hostMeta?.ui?.showTutorial !== false);
  const [retryCount, setRetryCount] = useState(0);

  // R1 実動タイマー: T14 の config に固有タイマーは無いため hostMeta.timeLimitMs のみ
  // （0 は「未設定」）。設定時のみ HUD に表示し、満了で失敗にする。
  const timeLimitMs =
    typeof hostMeta?.timeLimitMs === 'number' && hostMeta.timeLimitMs > 0
      ? hostMeta.timeLimitMs
      : null;
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: gameState === 'playing' && !gateOpen,
    onExpire: () => setGameState('fail')
  });

  // R1 ミス上限: config 明示値 ?? hostMeta.maxMistakes（>0 のみ）?? 既定 5。
  // 上限到達判定（next >= 上限）は従来式のまま。
  const effectiveMaxMistakes =
    typeof hostConfig?.maxMistakes === 'number'
      ? hostConfig.maxMistakes
      : typeof hostMeta?.maxMistakes === 'number' && hostMeta.maxMistakes > 0
        ? hostMeta.maxMistakes
        : defaultConfig.maxMistakes;

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setUserMap({});
    setSelectedCipher(null);
    setMistakes(0);
    setRevealedHints([]);
    setGameState('playing');
    setInputValue('');
    resetSubmission();
  }, [resetSubmission]);

  const originalChars = resolvedConfig.originalMessage.toUpperCase().split('');
  const uniqueCipherChars = [...new Set(originalChars.filter(c => /[A-Z]/.test(c)))];

  const checkSolution = useCallback(() => {
    for (const char of uniqueCipherChars) {
      if (!userMap[char] || userMap[char] !== substitutionMap[char]) {
        return false;
      }
    }
    return true;
  }, [userMap, substitutionMap, uniqueCipherChars]);

  const getCurrentDecoded = useCallback(() => {
    return originalChars.map(char => {
      if (!/[A-Z]/.test(char)) return char;
      return userMap[char] || '_';
    }).join('');
  }, [originalChars, userMap]);

  const handleCharSelect = (cipherChar) => {
    setSelectedCipher(cipherChar);
    setInputValue(userMap[cipherChar] || '');
  };

  const handleInputChange = (value) => {
    const char = value.toUpperCase().slice(-1);
    if (!/[A-Z]/.test(char) && char !== '') return;
    setInputValue(char);
  };

  const handleConfirmMapping = () => {
    if (!selectedCipher || !inputValue) return;

    const correctMapping = substitutionMap[selectedCipher];
    if (inputValue === correctMapping) {
      setUserMap(prev => ({ ...prev, [selectedCipher]: inputValue }));
      setSelectedCipher(null);
      setInputValue('');
      setShowEffect('correct');

      const newMap = { ...userMap, [selectedCipher]: inputValue };
      let solved = true;
      for (const char of uniqueCipherChars) {
        if (!newMap[char] || newMap[char] !== substitutionMap[char]) {
          solved = false;
          break;
        }
      }
      if (solved) {
        setTimeout(() => {
          setShowEffect('success');
          setGameState('success');
        }, 600);
      }
    } else {
      setShowEffect('incorrect');
      setMistakes(prev => {
        const next = prev + 1;
        if (next >= effectiveMaxMistakes) {
          setTimeout(() => setGameState('fail'), 500);
        }
        return next;
      });
      setInputValue('');
    }
  };

  const handleUseHint = () => {
    const availableHints = resolvedConfig.hints.filter((_, idx) => !revealedHints.includes(idx));
    if (availableHints.length === 0) return;

    const hintIndex = resolvedConfig.hints.findIndex((h, idx) => !revealedHints.includes(idx));
    if (hintIndex === -1) return;

    const hint = resolvedConfig.hints[hintIndex];
    setRevealedHints(prev => [...prev, hintIndex]);

    for (const [cipher, plain] of Object.entries(substitutionMap)) {
      if (plain === hint.plain.toUpperCase()) {
        setUserMap(prev => ({ ...prev, [cipher]: plain }));
        break;
      }
    }
  };

  const reset = () => {
    setUserMap({});
    setSelectedCipher(null);
    setMistakes(0);
    setRevealedHints([]);
    setGameState('playing');
    setInputValue('');
    setShowEffect(null);
    // R1: やり直しは retryCount に積算し（全 submit payload に同梱）、タイマーも計り直す
    setRetryCount((prev) => prev + 1);
    resetTimer();
    resetSubmission();
  };

  const progress = Object.keys(userMap).length;
  const total = uniqueCipherChars.length;
  const title = resolvedConfig.title ?? '暗号解読';

  return (
    <div className="t14-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {/* R1: 独自の上部固定バー（タイトル/ミス/進捗バー）を MinigameHud semantic へ置換
          （progress / mistakes はチップに統合）。timer は hostMeta 設定時のみ表示。
          ? ボタンでゲート再表示。 */}
      <MinigameHud
        title={title}
        icon="KeyRound"
        timer={remainingMs != null && timeLimitMs != null ? { remainingMs, totalMs: timeLimitMs } : null}
        progress={{ current: progress, total }}
        mistakes={effectiveMaxMistakes > 0 ? { current: mistakes, max: effectiveMaxMistakes } : null}
        onHelp={() => setGateOpen(true)}
      />

      {gameState === 'playing' && (
        <div className="absolute inset-0 flex flex-col pt-14 pb-4 px-3 overflow-y-auto">
          <div className="t14-panel backdrop-blur rounded-xl p-3 mb-3 flex-shrink-0">
            <div className="t14-eyebrow text-xs mb-2">暗号文（タップして対応を入力）</div>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {originalChars.map((char, idx) => {
                const isLetter = /[A-Z]/.test(char);
                const isSolved = isLetter && userMap[char];
                const isSelected = isLetter && selectedCipher === char;
                return (
                  <button
                    key={idx}
                    className={`min-w-[2.5rem] w-10 h-14 flex flex-col items-center justify-center rounded-lg transition-all ${
                      !isLetter
                        ? 't14-tile--spacer w-3'
                        : isSelected
                          ? 't14-tile--selected ring-2 scale-105'
                          : isSolved
                            ? 't14-tile--solved border-2'
                            : 't14-tile--idle border-2 active:scale-95'
                    }`}
                    onClick={() => isLetter && !isSolved && handleCharSelect(char)}
                    disabled={!isLetter || isSolved}
                  >
                    <span className="t14-tile-cipher text-[10px] leading-tight">{char}</span>
                    <span className={`text-lg font-bold leading-tight ${isSolved ? 't14-tile-plain--solved' : 't14-tile-plain'}`}>
                      {isLetter ? (userMap[char] || '?') : char}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="t14-panel backdrop-blur rounded-xl p-3 mb-3 flex-shrink-0">
            <div className="t14-decoded-label text-xs mb-1">現在の解読結果</div>
            <div className="t14-decoded-text text-base font-mono tracking-wider text-center break-all">
              {getCurrentDecoded()}
            </div>
          </div>

          {selectedCipher && (
            <div className="t14-input-panel backdrop-blur border-2 rounded-xl p-4 mb-3 flex-shrink-0">
              <div className="t14-input-prompt text-sm mb-3 font-medium">
                「{selectedCipher}」は何の文字？
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && inputValue && handleConfirmMapping()}
                  className="t14-input flex-1 border-2 rounded-xl px-4 py-3 text-center text-2xl font-bold uppercase outline-none"
                  maxLength={1}
                  placeholder="A-Z"
                  autoComplete="off"
                  autoCapitalize="characters"
                  inputMode="text"
                />
                <button
                  className={`px-6 py-3 rounded-xl font-bold text-lg transition-all ${
                    inputValue
                      ? 't14-confirm--active active:scale-95'
                      : 't14-confirm--disabled'
                  }`}
                  onClick={handleConfirmMapping}
                  disabled={!inputValue}
                >
                  決定
                </button>
              </div>
            </div>
          )}

          {revealedHints.length > 0 && (
            <div className="t14-hintbox border rounded-xl p-3 mb-3 flex-shrink-0">
              <div className="t14-hintbox-label text-xs mb-2">💡 使用したヒント</div>
              {revealedHints.map(idx => (
                <div key={idx} className="t14-hintbox-text text-sm">{resolvedConfig.hints[idx]?.hint}</div>
              ))}
            </div>
          )}

          <div className="mt-auto pt-2 flex-shrink-0">
            {resolvedConfig.hints.length > revealedHints.length && (
              <button
                className="t14-hint-btn w-full py-4 active:scale-[0.98] rounded-xl mb-2 text-base font-medium transition-all"
                onClick={handleUseHint}
              >
                💡 ヒントを使う（残り{resolvedConfig.hints.length - revealedHints.length}）
              </button>
            )}
          </div>
        </div>
      )}

      {/* Foundation: 成功モーダル（中央バッジ🎉 + タイトル + 説明 + 完了 を JudgementModal に集約。
          submit の呼び出し・ペイロード（mistakes / hintsUsed / userMap）は不変（R1 で retryCount を同梱）。
          rich 内容（解読結果 + 統計）は description 文字列に畳む（JudgementModal の description は string）。 */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title="暗号解読成功！"
        description={`解読結果「${resolvedConfig.decodedMessage}」 / ミス回数: ${mistakes} / ヒント使用: ${revealedHints.length}`}
        icon={<span className="text-5xl">🎉</span>}
        buttonLabel="完了"
        onSubmit={() => submit('success', { mistakes, hintsUsed: revealedHints.length, userMap, retryCount })}
      />

      {/* R1: 独自 fail オーバーレイ（やり直す / 失敗として送信 / 正解開示ボックス）を
          FailOverlay semantic へ移行。正解開示は subtitle に温存（semantic の範囲で表現）。
          retry=reset（retryCount 加算）、proceed=submit('fail')（文言は共通既定「未整理のまま進む」） */}
      {gameState === 'fail' && (
        <FailOverlay
          title="解読失敗"
          subtitle={`ミスが重なった。正解は「${resolvedConfig.decodedMessage}」`}
          onRetry={reset}
          onProceed={() => submit('fail', { mistakes, hintsUsed: revealedHints.length, userMap, retryCount })}
        />
      )}

      {/* R1 体験文法: 開始ゲート（タイトル＋ルール一行＋始める）。? ボタンで再表示できる */}
      {gateOpen && (
        <StartGate
          title={title}
          instruction="暗号のタイルに対応する文字を当てはめ、全文を解読せよ"
          icon="KeyRound"
          onStart={() => setGateOpen(false)}
        />
      )}

      {showEffect === 'correct' && (
        <SuccessEffect type="shine" duration={500} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'success' && (
        <SuccessEffect type="confetti" duration={1200} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'incorrect' && (
        <FailureEffect type="flash" duration={400} onComplete={() => setShowEffect(null)} />
      )}
    </div>
  );
};

export default Template14CipherV2;
